import React, { useEffect, useState } from 'react';
import { VoidFunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardProgress,
  Form,
  FormField,
  FormGroup,
  Input,
} from 'components/elements';
import { createResolver } from './LoginForm.validation';
import { LoginFormProps, LoginFormValue } from './types';
import { getIsAccessThroughSP6$ } from 'services/auth';

export const LoginForm: VoidFunctionComponent<LoginFormProps> = props => {
  const { disabled = false, loading = false, onSubmit } = props;
  const [searchParams] = useSearchParams();
  const refreshTokenFromUrl = searchParams.get('refreshToken');
  
  const [loadingStandalone, setLoadingStandalone] = useState(true);
  const [isStandalone, setIsStandalone] = useState<boolean | null>(null);

  // Check if running inside an iframe
  const isIframeMode = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  useEffect(() => {
    // Skip standalone checks if in iframe mode with token
    if (isIframeMode() && refreshTokenFromUrl) {
      setLoadingStandalone(false);
      return;
    }

    try {
      const accessResponse = getIsAccessThroughSP6$();
      const subscription = accessResponse.subscribe({
        next: (access) => {
          const accessAsBoolean = access === 'true';
          console.log("accessAsBoolean", accessAsBoolean);
          setIsStandalone(accessAsBoolean);
        },
        error: (err) => {
          console.error('Error fetching system name', err);
          setIsStandalone(false);
        },
      });

      return () => subscription.unsubscribe();
    } catch (err) {
      console.error('Error fetching config:', err);
      setIsStandalone(false);
    } finally {
      setLoadingStandalone(false);
    }
  }, [refreshTokenFromUrl]);

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
  } = useForm<LoginFormValue>({
    criteriaMode: 'all',
    defaultValues: {
      password: '',
      username: '',
    },
    mode: 'all',
    resolver: createResolver(),
  });
    // Wrapping the handleSubmit to ensure no promise is returned to Form's onSubmit handler
  const wrappedSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        // Call handleSubmit with the provided event, which will call onSubmit with form data
    handleSubmit(onSubmit)(event).catch(err => {
            // Handle any submission errors here (optional)
      console.error(err);
    });
  };

  // Show loading while fetching config
  if (loadingStandalone) {
    return <div>Loading...</div>;
  }

  // Hide form completely if in iframe mode with token
  if (isIframeMode() && refreshTokenFromUrl) {
    return null;
  }

  // Show error page if in standalone mode without iframe token
  if (isStandalone === true) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h1>Access Denied</h1>
        <p>This application is not available in standalone mode.</p>
      </div>
    );
  }

  // Normal login form
  return (
    <Form onSubmit={wrappedSubmit}>
      <Card>
        <CardHeader>Login</CardHeader>
        <CardProgress active={loading} />
        <CardContent>
          <FormGroup>
            <FormField disabled={disabled} error={errors.username} label="Username" vertical={true}>
              <Input disabled={disabled} {...register('username')} />
            </FormField>
            <FormField disabled={disabled} error={errors.password} label="Password" vertical={true}>
              <Input
                autoComplete="current-password"
                disabled={disabled}
                id="current-password"
                {...register('password')}
                type="password"
              />
            </FormField>
          </FormGroup>
        </CardContent>
        <CardActions>
          <Button disabled={disabled || !isValid} variant="primary">
            Login
          </Button>
        </CardActions>
      </Card>
    </Form>
  );
};